/**
 * CoverImageUpload
 * Single image upload component for resource cover image.
 * Replaces the multi-file MediaUploadPanel (gap C9-1 through C9-5).
 */

import { useState, useRef, useCallback } from 'react';
import { X, RefreshCw, ImageIcon } from 'lucide-react';
import { apiClient } from '@/core/api/client';

export type MediaFile = {
  id?: string;
  fileName: string;
  fileUrl: string;
  mimeType: string;
  fileSize?: number;
};

type CoverImageUploadProps = {
  coverImage: MediaFile | null;
  onCoverImageChange: (file: MediaFile | null) => void;
};

const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/jpg,image/png,image/gif,image/webp';

export function CoverImageUpload({ coverImage, onCoverImageChange }: CoverImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [failedFile, setFailedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = useCallback(
    async (file: File) => {
      if (!file.type.startsWith('image/')) {
        setUploadError('Only image files are accepted.');
        return;
      }
      setIsUploading(true);
      setUploadError(null);
      setFailedFile(null);

      const formData = new FormData();
      formData.append('file', file);

      try {
        const { data } = await apiClient.post('/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        onCoverImageChange({
          fileName: file.name,
          fileUrl: data.publicUrl,
          mimeType: file.type,
          fileSize: Math.round(file.size / 1024),
        });
      } catch {
        setUploadError('Upload failed. Please try again.');
        setFailedFile(file);
      } finally {
        setIsUploading(false);
      }
    },
    [onCoverImageChange],
  );

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await uploadFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) await uploadFile(file);
    },
    [uploadFile],
  );

  const handleRemove = () => {
    onCoverImageChange(null);
    setUploadError(null);
    setFailedFile(null);
  };

  const handleRetry = () => {
    if (failedFile) uploadFile(failedFile);
  };

  return (
    <div data-testid="cover-image-upload" className="space-y-3">
      <h3 className="text-sm font-medium text-gray-700">Cover Image</h3>

      {coverImage ? (
        <div className="relative rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
          <img
            src={coverImage.fileUrl}
            alt={coverImage.fileName}
            className="w-full h-48 object-cover"
          />
          <div className="absolute top-2 right-2 flex gap-1">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-1.5 bg-white/90 rounded-md shadow hover:bg-white transition-colors"
              aria-label="Replace cover image"
            >
              <RefreshCw className="h-4 w-4 text-gray-600" />
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="p-1.5 bg-white/90 rounded-md shadow hover:bg-white transition-colors"
              aria-label="Remove cover image"
            >
              <X className="h-4 w-4 text-gray-600" />
            </button>
          </div>
          <div className="px-3 py-2 text-xs text-gray-500 truncate">
            {coverImage.fileName}
            {coverImage.fileSize && <span className="ml-2">{coverImage.fileSize} KB</span>}
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            isDragOver
              ? 'border-[#026857] bg-[#026857]/5'
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          {isUploading ? (
            <RefreshCw className="h-8 w-8 mx-auto mb-2 text-gray-400 animate-spin" />
          ) : (
            <ImageIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          )}
          <p className="text-sm text-gray-600">
            {isUploading ? 'Uploading...' : 'Drag & drop or click to upload cover image'}
          </p>
          <p className="text-xs text-gray-400 mt-1">JPEG, PNG, GIF, or WebP</p>
        </div>
      )}

      <input
        ref={fileInputRef}
        data-testid="cover-image-input"
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        onChange={handleFileSelect}
        className="hidden"
      />

      {uploadError && (
        <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          <span>{uploadError}</span>
          <button
            type="button"
            onClick={handleRetry}
            className="flex items-center gap-1 ml-auto text-red-600 hover:text-red-800"
            aria-label="Retry"
          >
            <RefreshCw className="h-3 w-3" />
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
